import axios from 'axios';
import _get from 'lodash/get';
import * as types from '../constants/ActionTypes';
import Page from '../../lib/page';
import { trackPage } from '../../lib/analytics-helper';
import { fetchDefaultPCCArticles } from './action-default-PCC';
import urlResource from '../constants/UrlResources';
import { articleChannelTypes, CC3Channel } from '../../common/commonConstants';
import { resetOmniChannelPromptInfo } from '../../contactUs/actions/action-article';
import { filterRecommendedChannels } from '../../lib/globalHelper';
import { receivedOCPData, resetOCPData } from '../../common/ocpCardPrompt/action-ocpComponent';
import { isBotExperienceEnabled, isHomeExpTransactionLayoutEnabled } from '../../../experimentations/helper';
import {
  ECM_DOC_ID_KEY,
  ECM_COMPONENT_IDS_KEY,
  ECM_DOC_ID_DEFAULT_VALUE,
  ECM_COMPONENT_IDS_DEFAULT_VALUE,
} from '../../contactUs/constants/ecmFPTITagsKeys';

const BNAPromptURL = '/smarthelp/prompt-bna';

function requestPopularQuestions() {
  return {
    type: types.REQUEST_POPULAR_LIST,
  };
}

function receivePopularQuestions(response, experience, params) {
  const articles = _get(response, 'recommendedSolutions');
  return {
    type: types.RECEIVE_POPULAR_LIST,
    payload: {
      experience,
      articles,
      params,
    },
  };
}

function receivedPopularQuestionsNAA(response) {
  const articles = _get(response, 'recommendedSolutions');
  return {
    type: types.RECEIVED_POPULAR_LIST_NAA,
    payload: {
      articles,
    },
  };
}

function receivePopularQuestionsError() {
  return {
    type: types.RECEIVE_POPULAR_ERROR,
  };
}

// Send Impression event for each BNA article data in the popular article
// eslint-disable-next-line consistent-return
const BNAImpressionTracker = function (response) {
  if (!response || !Array.isArray(response.recommendedSolutions) || response.recommendedSolutions.length <= 0) {
    return null;
  }

  const BNAList = response.recommendedSolutions.filter((article) => article.channel === 'BNA');
  if (BNAList.length > 0) {
    // PCC prompt FPTI event
    BNAList.forEach((article) => {
      // get the ECM API tags from article data
      const fptiDocId = article[ECM_DOC_ID_KEY] || ECM_DOC_ID_DEFAULT_VALUE;
      const fptiComponentIds = article[ECM_COMPONENT_IDS_KEY] || ECM_COMPONENT_IDS_DEFAULT_VALUE;

      trackPage({
        id: 'PCCTrackingPage',
        link: 'smarthelp-home-pcc',
        hpid: article.articleId,
        help_doc_id: fptiDocId,
        help_component_ids: fptiComponentIds,
      });
    });

    // PCC/BNA prompt event for each BNA article
    return axios
      .post(BNAPromptURL, {
        _csrf: Page.getCSRFtoken(),
        BNAList: JSON.stringify(BNAList),
      })
      .catch(() => {});
  }
};

// Function to trigger CC3 articles impression
// eslint-disable-next-line consistent-return
const CC3ImpressionTracker = function (response) {
  if (!response || !Array.isArray(response.recommendedSolutions) || response.recommendedSolutions.length <= 0) {
    return null;
  }

  const CC3ArticlesList = response.recommendedSolutions.filter((article) => article.channel === CC3Channel);
  if (CC3ArticlesList.length > 0) {
    CC3ArticlesList.forEach((article) => {
      trackPage({ id: 'CC3TrackingPage', link: 'smarthelp-home-cc3', hpid: article.articleId });
    });
  }
};

// Function to trigger Omni articles impression
// eslint-disable-next-line consistent-return
const OmniImpressionTracker = function (response) {
  if (!response || !Array.isArray(response.recommendedSolutions) || response.recommendedSolutions.length <= 0) {
    return null;
  }

  const OmniArticlesList = filterRecommendedChannels(response.recommendedSolutions, articleChannelTypes.OMNI_CHANNEL);
  if (OmniArticlesList.length > 0) {
    OmniArticlesList.forEach((article) => {
      trackPage({ id: 'omniTrackingPage', link: 'smarthelp-home-omni-article', hpid: article.articleId });
    });
  }
};

function requestBNAPrompt(response) {
  BNAImpressionTracker(response);
  return {
    type: types.BNA_PROMPT_REQUEST,
  };
}

/**
 * Service call to get recommended solutions which includes BNA's
 * This service is being called from home index and contactUs index as we need to check for certain BNA's to display NAA alert.
 * After calling this service there are some other services which needs to be triggered only in Home page.
 * so added an If condition to perform those operations in Home page.
 *
 * @param experience
 * @param pathName
 * @returns dispatch functions
 */
export default function getPopularQuestions(experience, pathName = '') {
  if (!isBotExperienceEnabled() && !isHomeExpTransactionLayoutEnabled()) {
    return (dispatch, getState) => {
      dispatch(requestPopularQuestions());

      // Check NNA data only if logged in and if home page. omnichannel prompt is available  in all pages
      const isNNARequired = (Page.isLoggedIn() && pathName === urlResource.HOME) || false;
      // reseting update context elmo and intent article
      dispatch(resetOmniChannelPromptInfo());
      // for contact-US pagae
      dispatch(resetOCPData());
      const recommendUrl = `/smarthelp/recommended-solutions?isNNARequired=${isNNARequired}&pathname=${pathName}`;
      return axios
        .get(Page.setChannelForTechnicalExp(recommendUrl))
        .then((response) => {
          const {
            location: { pageName, pathname },
          } = getState();

          // Dispatch to Save OCP Data in store
          const ocpData = _get(response, 'data.ocp', {});

          const isNetNewAccount = response.data.isNNAEnabled || false;

          // perform the below operations only in Home Page.
          // Added this logic as we are calling recommended solutions from multi pages to display NAA
          // but these below operations need to be performed only in HOME page.
          if (pathname === `/${urlResource.HOME}`) {
            dispatch(receivedOCPData(ocpData));
            dispatch(receivePopularQuestions(response.data, experience, { isNetNewAccount, ocpData }));

            // BNA's will exists only for logged in users.
            if (Page.isLoggedIn()) {

              // Impressions for CC3 articles channel
              CC3ImpressionTracker(response.data);

              // Impression for Omni Articles
              OmniImpressionTracker(response.data);

              dispatch(requestBNAPrompt(response.data));

              fetchDefaultPCCArticles(dispatch, response.data);
            }
          } else if (pageName === 'contactUs') {
            dispatch(receivedOCPData(ocpData));

            dispatch(receivePopularQuestions(response.data, experience, { isNetNewAccount, ocpData }));
            // dispatch(receivedPopularQuestionsNAA(response.data));
          } else {
            // if not Home page check for NAA exists.
            dispatch(receivedPopularQuestionsNAA(response.data));
          }
        })
        .catch(() => {
          dispatch(receivePopularQuestionsError());
        });
    };
  }
  return () => {};
}
